<script setup lang="ts">
import { RouterLink } from 'vue-router'
import ProductMockup from '../components/ProductMockup.vue'
import { TAOBAO_PURCHASE_URL } from '../purchase'

const comparisonHighlights = [
  { title: '本地优先，不必先上传', detail: '素材检查与适配默认在本机完成，减少不必要的上传链路。' },
  { title: '账号不托管，发布权不转移', detail: '无需提交平台账号、Cookie 或会话，最终发布仍由用户在官方后台完成。' },
  { title: '风险先说明，不做黑箱', detail: '先展示真实媒体参数、规则依据和风险，再由用户决定处理方式。' },
  { title: '各平台独立成稿', detail: '画面、封面和文案分别保存，不用一套内容强行适配全部渠道。' },
  { title: '构图保留人工确认', detail: '裁剪、完整留白和模糊背景均可手动调整，避免自动处理替用户做决定。' },
  { title: '失败按平台恢复', detail: '单个平台失败只重试该平台，已经完成的结果继续保留。' },
]

const values = [
  { index: '1', title: '先检查再处理', detail: '展示真实媒体参数、平台风险与规则依据。', tone: 'warning' },
  { index: '2', title: '构图由你决定', detail: '裁剪、完整留白、模糊背景，支持手动调整。', tone: 'brand' },
  { index: '3', title: '分平台独立成稿', detail: '视频、封面、标题、正文和标签分别保存。', tone: 'brand' },
  { index: '4', title: '失败可单独恢复', detail: '已完成结果保留，只重试失败平台。', tone: 'teal' },
]
</script>

<template>
  <section class="home-hero">
    <div class="home-hero__copy">
      <h1><span>一份母版，</span><span>多端成稿</span></h1>
      <p>在本地检查、适配并导出抖音、小红书、快手、视频号和 B 站素材包。</p>
      <div class="home-hero__pills">
        <span class="ml-pill ml-pill--teal">素材默认本地处理</span>
        <span class="ml-pill">不接管平台账号</span>
        <span class="ml-pill home-hero__plain">用户最终确认</span>
      </div>
      <div class="home-hero__actions">
        <RouterLink class="ml-button" to="/download">下载安装包</RouterLink>
        <a data-purchase-link class="ml-button ml-button--secondary" :href="TAOBAO_PURCHASE_URL" target="_blank" rel="noopener noreferrer">前往淘宝购买</a>
      </div>
      <small>每个随机安装标识可领取一次 7 天完整功能试用，不采集硬件序列号；可随时升级正式授权。</small>
    </div>
    <ProductMockup />
  </section>

  <section class="home-comparison page-wrap">
    <header class="home-comparison__heading">
      <span class="ml-pill">发多多的差异</span>
      <h2>相比常见同类方案，更强调本地、透明和可控</h2>
      <p>不依赖账号托管或黑箱式批处理来换取“一键完成”，每一步都保留用户确认和独立恢复空间。</p>
    </header>
    <div class="home-comparison__grid">
      <article v-for="(highlight, index) in comparisonHighlights" :key="highlight.title" class="ml-card">
        <span class="comparison-index">{{ String(index + 1).padStart(2, '0') }}</span>
        <h3>{{ highlight.title }}</h3>
        <p>{{ highlight.detail }}</p>
      </article>
    </div>
  </section>

  <section class="home-values page-wrap">
    <h2>从导入到发布前准备，只做必要的事</h2>
    <div class="home-values__grid">
      <article v-for="value in values" :key="value.index" class="ml-card">
        <span class="value-index" :class="`value-index--${value.tone}`">{{ value.index }}</span>
        <h3>{{ value.title }}</h3>
        <p>{{ value.detail }}</p>
      </article>
    </div>
  </section>
</template>

<style scoped>
.home-hero {
  display: grid;
  grid-template-columns: 1.08fr 0.92fr;
  align-items: center;
  gap: 72px;
  width: min(1200px, calc(100% - 48px));
  margin: 0 auto;
  padding: 78px 0 46px;
}

.home-hero h1 {
  margin: 0 0 30px;
  font-size: clamp(44px, 5.5vw, 68px);
  letter-spacing: -0.06em;
}

.home-hero p {
  max-width: 650px;
  margin: 0;
  color: var(--ml-text-secondary);
  font-size: clamp(17px, 2vw, 21px);
  line-height: 1.8;
}

.home-hero__pills,
.home-hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
}

.home-hero__pills {
  margin: 42px 0 32px;
}

.home-hero__plain {
  border: 1px solid var(--ml-border);
  background: transparent;
  color: var(--ml-text-primary);
}

.home-hero__actions .ml-button {
  min-width: 190px;
}

.home-hero small {
  display: block;
  margin-top: 26px;
  color: var(--ml-warning);
}

.home-values {
  padding-top: 40px;
}

.home-comparison {
  padding-top: 54px;
  padding-bottom: 28px;
}

.home-comparison__heading {
  max-width: 820px;
  margin-bottom: 32px;
}

.home-comparison__heading h2 {
  margin: 18px 0 14px;
  font-size: clamp(28px, 3.5vw, 40px);
  letter-spacing: -0.035em;
}

.home-comparison__heading p,
.home-comparison__grid p {
  color: var(--ml-text-secondary);
  line-height: 1.8;
}

.home-comparison__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.home-comparison__grid article {
  min-height: 205px;
  padding: 26px;
}

.home-comparison__grid h3 {
  margin: 24px 0 14px;
  font-size: 18px;
}

.home-comparison__grid p {
  margin: 0;
  font-size: 14px;
}

.comparison-index {
  color: var(--ml-brand);
  font-weight: 800;
  letter-spacing: 0.08em;
}

.home-values h2 {
  margin: 0 0 30px;
  font-size: 32px;
}

.home-values__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.home-values article {
  min-height: 220px;
  padding: 26px 24px;
}

.home-values h3 {
  margin: 26px 0 34px;
  font-size: 17px;
}

.home-values p {
  color: var(--ml-text-secondary);
  font-size: 13px;
  line-height: 1.8;
}

.value-index {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  border-radius: 14px;
  background: var(--ml-brand-soft);
  color: var(--ml-brand-strong);
  font-size: 18px;
  font-weight: 800;
}

.value-index--warning {
  background: var(--ml-warning-soft);
  color: var(--ml-warning);
}

.value-index--teal {
  background: var(--ml-teal-soft);
  color: var(--ml-teal);
}

@media (max-width: 960px) {
  .home-hero {
    grid-template-columns: 1fr;
  }

  .home-values__grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .home-comparison__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 560px) {
  .home-hero {
    width: min(100% - 32px, 1200px);
    padding-top: 44px;
  }

  .home-hero h1 {
    font-size: clamp(36px, 10vw, 40px);
    line-height: 1.25;
  }

  .home-hero h1 span {
    display: block;
  }

  .home-values__grid {
    grid-template-columns: 1fr;
  }

  .home-comparison {
    width: min(100% - 32px, 1200px);
  }

  .home-comparison__grid {
    grid-template-columns: 1fr;
  }
}
</style>
